// Fiche d'un test utilisateur — le SCÉNARIO et ce qu'il donne.
//
// Le test a été CADRÉ ailleurs (objectif, type, personas visés, effectif) par un
// formulaire court. Ici vit l'observation elle-même : action par action, ce que
// la personne devait faire et ce qui est arrivé.
//
// LE RÉSULTAT N'EST PLUS SAISI. Il se déduit du scénario — réussi = 100,
// difficulté = 50, bloqué = 0, moyenne des lignes observées. Un test ne peut
// donc plus afficher 90 % pendant que la moitié de ses actions bloquent.
//
// Pourquoi une page et non un tiroir : un scénario se remplit pendant qu'on
// observe, il se relit, il se corrige. Un tiroir est un geste ; ceci est un
// plan de travail.
import { useMemo, useState } from 'react';
import { useDraftScenario, usePillars, useSaveScenario, useTest } from '../api/queries';
import type { ScenarioDraft, StepOutcome, UserTest } from '../api/types';
import { ApiRequestError } from '../api/client';
import { Button, Chip, chipState, EmptyState, ErrorBanner, Eyebrow, Select, Spinner } from '../components/ui';
import { TabBar, useTabs } from '../components/Tabs';
import { useCanWrite } from '../write/useCanWrite';
import {
  AddToTriangulationForm,
  DeleteTestAction,
  EditAction,
  NewInsightForm,
  NewPersonaForm,
  NewStudyForm,
  TestForm,
  TestPersonasForm,
  TextArea,
  WriteAction,
} from '../write/Forms';
import { Link, useRouter } from '../router';
import { personaAside, personaTitle } from '../personas/labels';
import { SeverityChip, StatusChip } from '../taxonomy/Chips';
import './project.css';

/** Seuils de lecture d'un taux de réussite — les mêmes que la liste et le
 *  tableau de bord. Un même chiffre ne doit pas changer de couleur d'un écran
 *  à l'autre. */
function successTone(rate: number): string {
  if (rate >= 80) return 'tone-good';
  if (rate >= 60) return 'tone-warn';
  return 'tone-bad';
}

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  if (cause instanceof Error) return cause.message;
  return 'Une erreur inattendue est survenue.';
}

/** Les questions telles que le serveur les a renvoyées, en forme éditable. */
// ---------------------------------------------------------------------------
// SCÉNARIO — l'observation, et le score qui s'en déduit
// ---------------------------------------------------------------------------

/** Ce qu'on affiche pour chaque issue. Une seule couleur saturée par état : ce
 *  sont des SIGNAUX de lecture, pas des surfaces. */
const OUTCOMES: Record<StepOutcome, { label: string; tone: string; short: string; shortPlural: string }> = {
  reussi: { label: 'Réussi', tone: 'tone-good', short: 'réussie', shortPlural: 'réussies' },
  // Le pluriel est PORTÉ, pas déduit : « en difficulté » ne prend pas de s, et
  // coller un « s » à l'aveugle sur toutes les formes donnait « en difficultés ».
  difficulte: { label: 'Difficulté', tone: 'tone-warn', short: 'en difficulté', shortPlural: 'en difficulté' },
  bloque: { label: 'Bloqué', tone: 'tone-bad', short: 'bloquée', shortPlural: 'bloquées' },
  non_teste: { label: 'Non testé', tone: 'tone-empty', short: 'non observée', shortPlural: 'non observées' },
};

const OUTCOME_ORDER: StepOutcome[] = ['reussi', 'difficulte', 'bloque', 'non_teste'];

/** Le scénario du serveur, en forme éditable. */
function toSteps(test: UserTest): ScenarioDraft[] {
  return (test.scenario ?? []).map((s) => ({
    action: s.action,
    outcome: s.outcome,
    comment: s.comment,
    pillarId: s.pillar_id,
  }));
}

const EMPTY_STEP: ScenarioDraft = { action: '', outcome: 'non_teste', comment: null, pillarId: null };

function ScenarioEditor({ projectId, test }: { projectId: string; test: UserTest }) {
  const server = useMemo(() => toSteps(test), [test]);
  const [items, setItems] = useState<ScenarioDraft[]>(server);
  const [notice, setNotice] = useState<string | null>(null);
  const pillars = usePillars(projectId);
  const save = useSaveScenario(projectId);
  const draft = useDraftScenario();
  const [dictating, setDictating] = useState(false);
  const [spoken, setSpoken] = useState('');

  // Le scénario affiché appartient au serveur tant qu'on n'a rien touché : si
  // une autre session le fait changer, l'écran doit suivre plutôt que de
  // conserver une copie périmée.
  const [base, setBase] = useState(server);
  if (base !== server && items === base) {
    setBase(server);
    setItems(server);
  }

  const dirty = items !== server;

  function patch(i: number, change: Partial<ScenarioDraft>) {
    setItems((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...change } : s)));
    setNotice(null);
  }

  function addRow() {
    setItems((prev) => [...prev, { ...EMPTY_STEP }]);
    setNotice(null);
  }

  function removeRow(i: number) {
    setItems((prev) => prev.filter((_, idx) => idx !== i));
    setNotice(null);
  }

  /** Déplacer une ligne : dans un scénario l'ORDRE est l'ordre de passation,
   *  pas un détail d'affichage — la même raison qui fait porter `position` au
   *  scénario : on n'amorce pas avant d'avoir observé. */
  function move(i: number, delta: number) {
    setItems((prev) => {
      const j = i + delta;
      const a = prev[i];
      const b = prev[j];
      // Le garde-fou n'est pas décoratif : `noUncheckedIndexedAccess` a raison,
      // un index hors bornes rendrait `undefined` et trouerait la liste.
      if (!a || !b) return prev;
      const next = prev.slice();
      next[i] = b;
      next[j] = a;
      return next;
    });
    setNotice(null);
  }

  /** La dictée devient des lignes AJOUTÉES au tableau — jamais enregistrées
   *  d'office : on relit, on corrige, puis « Enregistrer le scénario ». Les
   *  lignes vides du tableau sont remplacées plutôt que gardées devant. */
  async function generate() {
    try {
      const res = await draft.mutateAsync({ testId: test.id, text: spoken });
      if (res.steps.length === 0) {
        setNotice('Aucune action reconnue dans la dictée. Reformulez le parcours étape par étape.');
        return;
      }
      setItems((prev) => [...prev.filter((s) => s.action.trim().length > 0), ...res.steps]);
      setSpoken('');
      setDictating(false);
      setNotice(
        `${res.steps.length} action(s) ajoutée(s) `
        + (res.mode === 'model' ? 'par l’IA' : 'en découpant la dictée phrase par phrase')
        + '. Relisez-les, puis enregistrez le scénario.',
      );
    } catch {
      /* l'erreur est rendue sous le panneau */
    }
  }

  async function submit() {
    // Une ligne sans action est une ligne vide : le serveur l'écarte de toute
    // façon, autant ne pas l'envoyer et le DIRE plutôt que de la voir
    // disparaître sans explication.
    const kept = items.filter((s) => s.action.trim().length > 0);
    const dropped = items.length - kept.length;
    const res = await save.mutateAsync({ testId: test.id, steps: kept });
    setItems(kept);
    const score = res.result?.score;
    setNotice(
      `${res.count} ligne(s) enregistrée(s)`
      + (dropped > 0 ? ` — ${dropped} ligne(s) sans action écartée(s)` : '')
      + (score === null || score === undefined
        ? '. Aucune ligne observée : le test n’a pas encore de résultat.'
        : `. Résultat du test : ${Math.round(score)} %.`),
    );
  }

  return (
    <div className="scenario-editor">
      {dictating ? (
        <div className="scenario-dictation">
          <TextArea
            label="Décrivez le parcours à voix haute"
            value={spoken}
            onChange={setSpoken}
            rows={4}
            placeholder="D’abord il se connecte, puis il cherche le relevé du jour, ensuite il l’exporte en PDF…"
            hint="Cliquez sur le micro et décrivez les étapes dans l’ordre. Chaque étape deviendra une ligne du scénario."
          />
          <div className="scenario-dictation-actions">
            <Button variant="ghost" onClick={() => setDictating(false)}>
              Annuler
            </Button>
            <Button
              variant="accent"
              onClick={() => void generate()}
              loading={draft.isPending}
              disabled={!spoken.trim()}
            >
              Générer les actions
            </Button>
          </div>
          {draft.error ? <ErrorBanner>{errorMessage(draft.error)}</ErrorBanner> : null}
        </div>
      ) : null}
      <div className="zy-table-cadre">
        <table className="zy-table scenario-table">
          <thead>
            <tr>
              <th className="col-num">#</th>
              <th>Action attendue</th>
              <th>Ce qui est arrivé</th>
              <th>Pilier</th>
              <th>Commentaire</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {items.map((s, i) => (
              <tr key={i}>
                <td className="col-num">{i + 1}</td>
                <td>
                  <input
                    className="zy-input"
                    value={s.action}
                    placeholder="Trouver le bouton d’export"
                    onChange={(e) => patch(i, { action: e.target.value })}
                  />
                </td>
                <td>
                  <Select
                    className={`${OUTCOMES[s.outcome].tone}`}
                    value={s.outcome}
                    onChange={(e) => patch(i, { outcome: e.target.value as StepOutcome })}
                  >
                    {OUTCOME_ORDER.map((o) => (
                      <option key={o} value={o}>
                        {OUTCOMES[o].label}
                      </option>
                    ))}
                  </Select>
                </td>
                <td>
                  {/* Facultatif, et c'est voulu : rattacher une action à un
                      pilier la fait peser sur la note d'adoption. On n'invente
                      aucun rattachement par ressemblance de libellé. */}
                  <Select
                   
                    value={s.pillarId ?? ''}
                    onChange={(e) => patch(i, { pillarId: e.target.value || null })}
                  >
                    <option value="">— Aucun</option>
                    {(pillars.data ?? []).map((pl) => (
                      <option key={pl.id} value={pl.id}>
                        {pl.name}
                      </option>
                    ))}
                  </Select>
                </td>
                <td>
                  <input
                    className="zy-input"
                    value={s.comment ?? ''}
                    placeholder="A cherché dans le menu Fichier pendant 40 s."
                    onChange={(e) => patch(i, { comment: e.target.value || null })}
                  />
                </td>
                <td className="row-actions">
                  <Button variant="link" size="s" className="u-push-end" type="button" onClick={() => move(i, -1)} disabled={i === 0}>
                    ↑
                  </Button>
                  <Button variant="link" size="s" className="u-push-end"
                    type="button"
                    onClick={() => move(i, 1)}
                    disabled={i === items.length - 1}
                  >
                    ↓
                  </Button>
                  <Button variant="link-danger" size="s" type="button" onClick={() => removeRow(i)}>
                    Retirer
                  </Button>
                </td>
              </tr>
            ))}
            {items.length === 0 ? (
              <tr>
                <td colSpan={6} className="card-note">
                  Aucune action décrite. Une ligne = une chose que la personne devait faire.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div className="card-foot">
        <Button variant="ghost" onClick={addRow}>
          Ajouter une action
        </Button>
        {dictating ? null : (
          <Button variant="ghost" onClick={() => setDictating(true)}>
            Dicter le scénario
          </Button>
        )}
        <Button variant="accent" onClick={() => void submit()} loading={save.isPending} disabled={!dirty}>
          Enregistrer le scénario
        </Button>
      </div>

      {notice ? <p className="zy-texte-muet">{notice}</p> : null}
      {save.error ? <ErrorBanner>{errorMessage(save.error)}</ErrorBanner> : null}
    </div>
  );
}

/** Le même tableau, en lecture seule — pour qui ne peut pas écrire sur ce
 *  projet. Mêmes colonnes, sinon ce n'est pas le même document. */
function ScenarioReadOnly({ test }: { test: UserTest }) {
  const steps = test.scenario ?? [];
  if (steps.length === 0) {
    return <p className="card-note">Aucune action n’a encore été décrite pour ce test.</p>;
  }
  return (
    <div className="zy-table-cadre">
      <table className="zy-table scenario-table">
        <thead>
          <tr>
            <th className="col-num">#</th>
            <th>Action attendue</th>
            <th>Ce qui est arrivé</th>
            <th>Commentaire</th>
          </tr>
        </thead>
        <tbody>
          {steps.map((s, i) => (
            <tr key={s.id}>
              <td className="col-num">{i + 1}</td>
              <td>{s.action}</td>
              <td>
                <Chip state={chipState(OUTCOMES[s.outcome].tone)}>{OUTCOMES[s.outcome].label}</Chip>
              </td>
              <td>{s.comment ?? <span className="tone-empty">—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ---------------------------------------------------------------------------
// LA FICHE
// ---------------------------------------------------------------------------

// Comme une étude : le protocole (cadrage et scénario), les participants, puis
// le résultat et ce que le test a produit.
type TestTab = 'protocole' | 'participants' | 'resultat';
const TEST_TABS: readonly TestTab[] = ['protocole', 'participants', 'resultat'];

export function TestDetailScreen({ projectId, testId }: { projectId: string; testId: string }) {
  const query = useTest(projectId, testId);
  const canWrite = useCanWrite(projectId);
  const { navigate } = useRouter();
  const backTo = `/projet/${encodeURIComponent(projectId)}/tests`;
  const [tab, setTab] = useTabs<TestTab>('test', TEST_TABS, 'protocole');

  if (query.isPending) {
    return (
      <main className="screen">
        <div className="screen-loading">
          <Spinner label="Chargement du test" />
        </div>
      </main>
    );
  }

  if (query.isError) {
    return (
      <main className="screen">
        <Link to={backTo} className="zy-retour">
          ← Tests utilisateurs
        </Link>
        <ErrorBanner>{errorMessage(query.error)}</ErrorBanner>
        <EmptyState
          title="Test introuvable dans votre périmètre"
          description="Il n’existe pas, ou il appartient à un projet auquel vous n’avez pas accès."
        />
      </main>
    );
  }

  const test = query.data;
  const insights = test.insights ?? [];
  const studies = test.studies ?? [];
  const produced = insights.length + studies.length;
  const counts = test.scenario_counts;

  return (
    <main className="screen">
      <Link to={backTo} className="zy-retour">
        ← Tests utilisateurs
      </Link>

      <div className="zy-page-head__texte">
        <Eyebrow>{test.code ?? test.id}</Eyebrow>
        <h1 className="t-h1">{test.objective ?? 'Sans objectif renseigné'}</h1>
        <div className="test-head-chips">
          <StatusChip value={test.status} />
          <Chip>
            {test.scenario_total} action{test.scenario_total > 1 ? 's' : ''}
          </Chip>
          {test.scenario_score === null ? null : (
            <Chip state={chipState(successTone(test.scenario_score))}>
              {Math.round(test.scenario_score)} %
            </Chip>
          )}
        </div>
        {canWrite ? (
          <div className="screen-actions">
            <EditAction
              title="Modifier le cadrage"
              description="Objectif, type, prototype, statut. Personas et participants se modifient dans leur bloc."
            >
              {(close) => (
                <TestForm projectId={projectId} existing={test} onDone={close} />
              )}
            </EditAction>
            {/* Supprimer depuis la fiche : on repart vers la liste, sinon
                l'écran resterait sur un test qui n'existe plus. */}
            <DeleteTestAction
              projectId={projectId}
              test={test}
              onDeleted={() => navigate(backTo)}
            />
          </div>
        ) : null}
      </div>

      <TabBar
        label="Sections du test"
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'protocole', label: 'Protocole', count: test.scenario_total || null },
          { id: 'participants', label: 'Participants' },
          { id: 'resultat', label: 'Résultat', count: produced || null },
        ]}
      />

      {/* ---- Le protocole : le cadrage, puis le scénario ---- */}
      {tab === 'protocole' ? (
      <>
      <section className="test-block">
        <h2 className="t-h3">Cadrage</h2>
        <dl className="facts">
          {test.type ? (
            <div>
              <dt>Type</dt>
              <dd>{test.type}</dd>
            </div>
          ) : null}
          {test.period ? (
            <div>
              <dt>Période</dt>
              <dd>{test.period}</dd>
            </div>
          ) : null}
          {test.product_url ? (
            <div>
              <dt>Prototype</dt>
              <dd>
                {/* Le serveur n'accepte que http(s) : le lien est sûr à ouvrir. */}
                <a href={test.product_url} target="_blank" rel="noopener noreferrer" className="proto-link">
                  {test.product_url.replace(/^https?:\/\//, '')}
                </a>
              </dd>
            </div>
          ) : null}
          {test.target ? (
            <div>
              <dt>Cible</dt>
              <dd>{test.target}</dd>
            </div>
          ) : null}
        </dl>
        {test.context ? <p className="card-note">{test.context}</p> : null}
      </section>
      <section className="test-block">
        <h2 className="t-h3">Scénario de test</h2>
        <p className="zy-intro">
          Une ligne par action attendue : ce que la personne devait faire, ce qui est arrivé, et ce
          qu’on en retient. C’est ce tableau qui donne son résultat au test.
        </p>
        {canWrite ? (
          <ScenarioEditor projectId={projectId} test={test} key={test.id} />
        ) : (
          <ScenarioReadOnly test={test} />
        )}
      </section>
      </>
      ) : null}

      {/* ---- Les personas visés, lisibles et modifiables ici ---- */}
      {tab === 'participants' ? (
      <section className="test-block">
        <div className="block-head">
          <h2 className="t-h3">Participants</h2>
          {canWrite ? (
            <EditAction
              title="Participants du test"
              description="Qui ce test cherche à observer, et combien de personnes le passeront. Rattacher un persona ne le modifie pas."
            >
              {(close) => <TestPersonasForm projectId={projectId} test={test} onDone={close} />}
            </EditAction>
          ) : null}
        </div>
        <p className="zy-intro">
          {test.participants
            ? `${test.participants} participant${test.participants > 1 ? 's' : ''} prévu${test.participants > 1 ? 's' : ''}.`
            : 'Nombre de participants non renseigné.'}{' '}
          Le nombre ne dit pas QUI est testé : les personas, si.
        </p>
        {test.personas.length > 0 ? (
          <ul className="persona-rows">
            {test.personas.map((p) => (
              <li key={p.id} className="persona-row">
                <span
                  className="persona-chip-mark"
                  style={p.accent ? { background: p.accent } : undefined}
                >
                  {p.initials ?? p.name.slice(0, 2).toUpperCase()}
                </span>
                <span>
                  <strong>{personaTitle(p)}</strong>
                  {personaAside(p) || p.pole ? (
                    <span className="persona-row-meta">
                      {' '}
                      · {[personaAside(p), p.pole].filter(Boolean).join(' · ')}
                    </span>
                  ) : null}
                </span>
                <span className="persona-row-stats">
                  {p.nugget_count ?? 0} faits · {p.session_count ?? 0} sessions
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <>
            <p className="card-note">Aucun persona visé n’est rattaché à ce test.</p>
            {canWrite ? (
              <div className="screen-actions">
                <WriteAction
                  label="Créer un persona"
                  title="Décrire un persona"
                  description="Vous pourrez ensuite le rattacher à ce test sans quitter cette page."
                >
                  {(close) => <NewPersonaForm projectId={projectId} onDone={close} />}
                </WriteAction>
              </div>
            ) : null}
          </>
        )}
      </section>
      ) : null}

      {/* ---- Le résultat, DÉDUIT du scénario, puis ce que le test a produit ---- */}
      {tab === 'resultat' ? (
      <>
      <section className="test-block">
        <h2 className="t-h3">Résultat</h2>
        <p className="zy-intro">
          Calculé, jamais saisi : réussi = 100, difficulté = 50, bloqué = 0, moyenne des actions
          observées. Une action « non testée » n’entre pas dans le calcul — ne pas avoir observé
          n’est pas avoir observé un échec.
        </p>

        <p className="result-headline">
          {test.scenario_score === null ? (
            <>
              <span className="big-inline tone-empty">—</span>
              <span className="result-detail-inline">
                {test.scenario_total === 0
                  ? 'aucune action décrite'
                  : 'aucune action observée pour l’instant'}
              </span>
            </>
          ) : (
            <>
              <span className={`big-inline ${successTone(test.scenario_score)}`}>
                {Math.round(test.scenario_score)}
                <span className="big-inline-unit">%</span>
              </span>
              <span className="result-detail-inline">
                sur {test.scenario_observed} action{test.scenario_observed > 1 ? 's' : ''} observée
                {test.scenario_observed > 1 ? 's' : ''}
                {test.scenario_total > test.scenario_observed
                  ? ` — ${test.scenario_total - test.scenario_observed} encore non testée${
                      test.scenario_total - test.scenario_observed > 1 ? 's' : ''
                    }`
                  : ''}
              </span>
            </>
          )}
        </p>

        {/* Le détail par issue : 66 % venu de deux réussites et un blocage
            n'appelle pas le même arbitrage que 66 % venu de trois passages en
            difficulté. Le chiffre seul efface cette différence. */}
        {test.scenario_total > 0 ? (
          <ul className="outcome-breakdown">
            {OUTCOME_ORDER.map((o) =>
              counts[o] > 0 ? (
                <li key={o} className="outcome-tally">
                  <span className={`outcome-tally-n ${OUTCOMES[o].tone}`}>{counts[o]}</span>
                  <span className="outcome-tally-label">
                    {counts[o] > 1
                      ? `actions ${OUTCOMES[o].shortPlural}`
                      : `action ${OUTCOMES[o].short}`}
                  </span>
                </li>
              ) : null,
            )}
          </ul>
        ) : null}

        {/* Mesures saisies AVANT que le résultat ne se déduise du scénario (017).
            Elles ne portent plus la note du test, mais elles restent des PREUVES
            citables et pèsent sur les piliers : on ne les efface pas d'un écran
            tant qu'elles existent. Un test neuf n'en a aucune, ce bloc
            disparaît alors de lui-même. */}
        {test.features.length > 0 ? (
          <>
          <h3 className="subhead">Fonctionnalités mesurées</h3>
          <ul className="feature-outcomes">
            {test.features.map((f) => (
              <li key={f.id} className="feature-outcome">
                <span className="feature-name">{f.name}</span>
                <span className={`feature-score ${successTone(Number(f.score))}`}>
                  {Math.round(Number(f.score))}%
                </span>
                {f.pillar_id ? null : (
                  <span className="feature-unlinked" title="Non rattachée à un pilier">
                    hors score
                  </span>
                )}
                {canWrite ? (
                  <span className="feature-outcome-actions">
                    <WriteAction
                      label="Créer un insight"
                      title="Créer un insight depuis cette mesure"
                      description="La fonctionnalité mesurée sera citée comme preuve — un taux de réussite établit une conclusion au même titre qu’un verbatim."
                    >
                      {(close) => (
                        <NewInsightForm
                          projectId={projectId}
                          onDone={close}
                          defaults={{
                            title: `${f.name} : ${Math.round(Number(f.score))} % de réussite`,
                            ...(f.pillar_id ? { pillarId: f.pillar_id } : {}),
                          }}
                          testFeatures={test.features.map((x) => ({
                            id: x.id,
                            name: x.name,
                            score: Number(x.score),
                            testCode: test.code,
                          }))}
                          preselectedFeatureIds={[f.id]}
                        />
                      )}
                    </WriteAction>
                    <WriteAction
                      label="Ajouter à un croisement"
                      title="Verser cette mesure dans une triangulation"
                      description="Le taux mesuré rejoint un croisement quali / quanti, avec ce test déclaré comme source."
                    >
                      {(close) => (
                        <AddToTriangulationForm
                          projectId={projectId}
                          test={test}
                          feature={{
                            id: f.id,
                            name: f.name,
                            score: Number(f.score),
                            pillar_id: f.pillar_id,
                          }}
                          onDone={close}
                        />
                      )}
                    </WriteAction>
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
          </>
        ) : null}
      </section>

      {/* ---- La boucle de retour : ce que le test a produit ---- */}
      <section className="test-block">
        <div className="block-head">
          <h2 className="t-h3">Ce que ce test a produit</h2>
          {canWrite ? (
            <WriteAction
              label="Créer une étude depuis ce test"
              title="Nouvelle étude, issue de ce test"
              description="Ce que le test a révélé mérite souvent d’être creusé autrement. Le lien vers ce test sera conservé."
            >
              {(close) => (
                <NewStudyForm
                  projectId={projectId}
                  onDone={close}
                  originTest={{
                    id: test.id,
                    code: test.code,
                    objective: test.objective,
                    target: test.target,
                  }}
                />
              )}
            </WriteAction>
          ) : null}
        </div>

        {produced === 0 ? (
          <p className="card-note">
            Ce test n’a encore rien produit. Un test qui ne débouche ni sur un insight ni sur une
            étude est une mesure sans suite.
          </p>
        ) : (
          <>
            {insights.length > 0 ? (
              <ul className="produced-list">
                {insights.map((i) => (
                  <li key={i.id} className="produced-item">
                    <Link to={`/projet/${encodeURIComponent(projectId)}/insights`}>{i.title}</Link>
                    <SeverityChip value={i.severity} />
                    <StatusChip value={i.treatment} />
                  </li>
                ))}
              </ul>
            ) : null}
            {studies.length > 0 ? (
              <ul className="produced-list">
                {studies.map((s) => (
                  <li key={s.id} className="produced-item">
                    <Link to={`/projet/${encodeURIComponent(projectId)}/etudes`}>
                      {s.objective ?? s.code ?? s.id}
                    </Link>
                    <StatusChip value={s.status} />
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        )}
      </section>
      </>
      ) : null}
    </main>
  );
}
